import { MapRef } from "react-map-gl/maplibre";

import { Raster } from "@nens/lizard/api-v4";
import { arrayMax, arrayMin, removeNullUndefined } from "@nens/shared-utils";

interface Dimensions {
  width: number; // Pixels
  height: number;
  boundingBox: [
    [number, number], // west, north WGS84
    [number, number], // east, north
    [number, number], // east, south
    [number, number] // west, south
  ];
}

const MAX_PIXELS = 600 * 600; // So rain radar is OK

export function getOptimalImageDimensions(raster: Raster, map: MapRef): Dimensions {
  if (raster.spatial_bounds === null) {
    throw new Error("Raster.spatial_bounds is null");
  }

  // First, the ideal -- if we can get the whole raster in source resolution
  const wholeRasterSourceResolution = dimensionsOfWholeRasterInSourceResolution(raster);
  if (
    wholeRasterSourceResolution !== null &&
    wholeRasterSourceResolution.width * wholeRasterSourceResolution.height <= MAX_PIXELS
  ) {
    return wholeRasterSourceResolution;
  }

  // Other options -- the whole raster on the current view, or the whole current view
  const wholeRasterMapResolution = dimensionsOfWholeRasterInWindowResolution(raster, map);
  const wholeWindowDimensions = dimensionsOfWholeWindow(map);

  const options = removeNullUndefined([
    wholeRasterSourceResolution,
    wholeRasterMapResolution,
    wholeWindowDimensions,
  ]);
  const whichOption = arrayMin(options, (dimensions) => dimensions.width * dimensions.height);

  const choice = options[whichOption];

  while (choice.width * choice.height > MAX_PIXELS) {
    choice.width = Math.floor(choice.width * 0.8);
    choice.height = Math.floor(choice.height * 0.8);
  }

  return choice;
}

function dimensionsOfWholeRasterInSourceResolution(raster: Raster): Dimensions | null {
  if (raster.spatial_bounds === null) {
    throw new Error("Raster.spatial_bounds is null");
  }
  // These are in WGS84
  const { west, east, south, north } = raster.spatial_bounds;

  // 90 degrees in WGS84 cannot be translated to Web Mercator
  if (south === -90 || north === 90) {
    return null;
  }

  // These are in the raster's source projection
  const { origin_x, origin_y, upper_bound_x, upper_bound_y, pixelsize_x, pixelsize_y } = raster;

  if (
    origin_x === null ||
    origin_y === null ||
    pixelsize_x === null ||
    pixelsize_y === null ||
    upper_bound_x === null ||
    upper_bound_y === null
  ) {
    return null;
  }

  const width = Math.round(Math.abs((upper_bound_x - origin_x) / pixelsize_x));
  const height = Math.round(Math.abs((upper_bound_y - origin_y) / pixelsize_y));

  return {
    width,
    height,
    boundingBox: [
      [west, north],
      [east, north],
      [east, south],
      [west, south],
    ],
  };
}

function dimensionsOfWholeRasterInWindowResolution(raster: Raster, map: MapRef): Dimensions | null {
  if (raster.spatial_bounds === null) {
    throw new Error("Raster.spatial_bounds is null");
  }
  // These are in WGS84
  const { west, east, south, north } = raster.spatial_bounds;

  // 90 degrees in WGS84 cannot be translated to Web Mercator
  if (south === -90 || north === 90) {
    return null;
  }

  const mapPoints = [
    map.project([west, north]),
    map.project([east, north]),
    map.project([east, south]),
    map.project([west, south]),
  ];

  const xs = mapPoints.map((point) => point.x);
  const ys = mapPoints.map((point) => point.y);

  const minX = xs[arrayMin(xs)];
  const minY = ys[arrayMin(ys)];
  const maxX = xs[arrayMax(xs)];
  const maxY = ys[arrayMax(ys)];

  const nw = map.unproject([minX, minY]);
  const ne = map.unproject([maxX, minY]);
  const se = map.unproject([maxX, maxY]);
  const sw = map.unproject([minX, maxY]);

  const result: Dimensions = {
    width: Math.round(maxX - minX),
    height: Math.round(maxY - minY),
    boundingBox: [
      [nw.lng, nw.lat],
      [ne.lng, ne.lat],
      [se.lng, se.lat],
      [sw.lng, sw.lat],
    ],
  };

  return result;
}

// "Traditional WMS"
function dimensionsOfWholeWindow(map: MapRef): Dimensions {
  const width = Math.ceil(map.getContainer().getBoundingClientRect().width);
  const height = Math.ceil(map.getContainer().getBoundingClientRect().height);

  const nw = map.unproject([0, 0]);
  const ne = map.unproject([width - 1, 0]);
  const se = map.unproject([width - 1, height - 1]);
  const sw = map.unproject([0, height - 1]);

  const result: Dimensions = {
    width,
    height,
    boundingBox: [
      [nw.lng, nw.lat],
      [ne.lng, ne.lat],
      [se.lng, se.lat],
      [sw.lng, sw.lat],
    ],
  };

  return result;
}
